import { ref, computed, shallowReadonly, onScopeDispose, type Ref } from 'vue';
import type {
  Player,
  Letter,
  SelectionStatus,
  PlayerRoundResult,
  PlayerTotalScore,
  GameSnapshot,
  GameSnapshotState,
  BestWord,
} from '@/types/generated';
import { GAME_CONFIG } from '@/config/constants';
import { getMetrics } from '@/telemetry/metrics';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

// 'match-found' intentionally appears in both AppGameState and MatchmakingScreenState
// (types/websocket.ts). They are separate state machines at different layers:
// AppGameState drives App.vue top-level routing; MatchmakingScreenState drives
// GameMatchmaking.vue internal navigation.
export type AppGameState =
  | 'matchmaking'
  | 'match-found'
  | 'selecting-letters'
  | 'in-progress'
  | 'round-finished'
  | 'awaiting-new-round'
  | 'game-over'
  | 'error';

export interface GameStartData {
  gameId: string;
  players: Player[];
  currentPlayerId: string;
}

export interface MatchFoundInfo {
  opponentName: string;
  isBotGame: boolean;
  countdownDurationMs: number;
}

export interface UseGameFlowOptions {
  stopTimer: () => void;
  onResetTimerPhase: () => void;
  sendMessage: (msg: import('@/types/generated').ClientMessage) => boolean;
}

export interface UseGameFlowReturn {
  gameState: Readonly<Ref<AppGameState>>;
  gameId: Readonly<Ref<string>>;
  players: Readonly<Ref<Player[]>>;
  currentPlayerId: Readonly<Ref<string>>;
  selectedLetters: Readonly<Ref<Letter[]>>;
  roundResults: Readonly<Ref<PlayerRoundResult[] | null>>;
  errorMessage: Readonly<Ref<string>>;
  newRoundPending: Readonly<Ref<boolean>>;
  opponentWantsNewRound: Readonly<Ref<boolean>>;
  cumulativeScores: Readonly<Ref<PlayerTotalScore[]>>;
  roundNumber: Readonly<Ref<number>>;
  wordSubmitted: Readonly<Ref<boolean>>;
  opponentSubmitted: Readonly<Ref<boolean>>;
  selectorPlayerId: Readonly<Ref<string | null>>;
  selectionEnabled: Readonly<Ref<boolean | null>>;
  vowelButtonEnabled: Readonly<Ref<boolean | null>>;
  consonantButtonEnabled: Readonly<Ref<boolean | null>>;
  selectionStatus: Readonly<Ref<SelectionStatus | null>>;
  roundEndedEarly: Readonly<Ref<boolean>>;
  autoStartMatchmaking: Readonly<Ref<boolean>>;
  matchFoundInfo: Readonly<Ref<MatchFoundInfo | null>>;
  bestWord: Readonly<Ref<BestWord | null>>;
  queueAvailable: Readonly<Ref<boolean>>;
  showRaceConditionDialog: Readonly<Ref<boolean>>;
  recordMatchFound: (info: MatchFoundInfo) => void;
  handleSnapshot: (snapshot: GameSnapshot) => void;
  handlePlayAgain: () => void;
  handleReturnHome: () => void;
  handleFindNewMatch: () => void;
  handleError: (error: string) => void;
  handleReconnect: () => void;
  handleQueueStatus: (available: boolean) => void;
  handleTryMatchWaitingPlayer: () => void;
  handleWaitingPlayerNotFound: () => void;
  handlePlaySolo: () => void;
  handleFindNewMatchFromDialog: () => void;
}

export function useGameFlow(options: UseGameFlowOptions): UseGameFlowReturn {
  const { stopTimer, onResetTimerPhase, sendMessage } = options;

  const gameState = ref<AppGameState>('matchmaking');
  const autoStartMatchmaking = ref(false);
  const gameId = ref<string>('');
  const players = ref<Player[]>([]);
  const currentPlayerId = ref<string>('');
  const selectedLetters = ref<Letter[]>([]);
  const roundResults = ref<PlayerRoundResult[] | null>(null);
  const errorMessage = ref<string>('');
  const cumulativeScores = ref<PlayerTotalScore[]>([]);
  const roundNumber = ref<number>(1);
  const newRoundPending = computed(() => gameState.value === 'awaiting-new-round');
  const opponentWantsNewRound = ref<boolean>(false);
  const wordSubmitted = ref<boolean>(false);
  const opponentSubmitted = ref<boolean>(false);
  const selectorPlayerId = ref<string | null>(null);
  const selectionEnabled = ref<boolean | null>(null);
  const vowelButtonEnabled = ref<boolean | null>(null);
  const consonantButtonEnabled = ref<boolean | null>(null);
  const selectionStatus = ref<SelectionStatus | null>(null);
  const roundEndedEarly = ref<boolean>(false);
  const matchFoundInfo = ref<MatchFoundInfo | null>(null);
  const bestWord = ref<BestWord | null>(null);
  const queueAvailable = ref<boolean>(false);
  const showRaceConditionDialog = ref<boolean>(false);
  const tryingMatch = ref<boolean>(false);

  let matchFoundTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let matchFoundTimestamp: number | null = null;
  let selectionPhaseStart: number | null = null;
  let tryMatchTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let awaitingEndGameResponse = false;
  const TRY_MATCH_TIMEOUT_MS = 10_000;

  const clearMatchFoundTimeout = () => {
    if (matchFoundTimeoutId) {
      clearTimeout(matchFoundTimeoutId);
      matchFoundTimeoutId = null;
    }
    matchFoundTimestamp = null;
  };

  const clearTryMatchTimeout = () => {
    if (tryMatchTimeoutId) {
      clearTimeout(tryMatchTimeoutId);
      tryMatchTimeoutId = null;
    }
  };

  const recordMatchFound = (info: MatchFoundInfo) => {
    if (gameState.value !== 'matchmaking') return;
    clearTryMatchTimeout();
    tryingMatch.value = false;
    matchFoundTimestamp = Date.now();
    matchFoundInfo.value = info;
    gameState.value = 'match-found';
  };

  const resetGameState = () => {
    clearMatchFoundTimeout();
    clearTryMatchTimeout();
    matchFoundTimestamp = null;
    autoStartMatchmaking.value = false;
    gameState.value = 'matchmaking';
    gameId.value = '';
    players.value = [];
    currentPlayerId.value = '';
    selectedLetters.value = [];
    roundResults.value = null;
    errorMessage.value = '';
    opponentWantsNewRound.value = false;
    cumulativeScores.value = [];
    roundNumber.value = 1;
    wordSubmitted.value = false;
    opponentSubmitted.value = false;
    selectorPlayerId.value = null;
    selectionEnabled.value = null;
    vowelButtonEnabled.value = null;
    consonantButtonEnabled.value = null;
    selectionStatus.value = null;
    roundEndedEarly.value = false;
    matchFoundInfo.value = null;
    bestWord.value = null;
    queueAvailable.value = false;
    showRaceConditionDialog.value = false;
    tryingMatch.value = false;
    onResetTimerPhase();
    stopTimer();
  };

  /**
   * Apply the full state from a snapshot to all reactive refs.
   */
  const applyState = (state: GameSnapshotState) => {
    gameId.value = state.game_id;
    currentPlayerId.value = state.player_id;
    players.value = state.players;
    roundNumber.value = state.round_number;
    selectedLetters.value = state.selected_letters;
    cumulativeScores.value = state.cumulative_scores;
    opponentWantsNewRound.value = state.opponent_wants_new_round;
    wordSubmitted.value = state.player_submitted;
    opponentSubmitted.value = state.opponent_submitted;
    selectorPlayerId.value = state.selector_player_id ?? null;
    selectionEnabled.value = state.selection_enabled ?? null;
    vowelButtonEnabled.value = state.vowel_button_enabled ?? null;
    consonantButtonEnabled.value = state.consonant_button_enabled ?? null;
    selectionStatus.value = state.selection_status ?? null;
    roundEndedEarly.value = state.round_ended_early;
    bestWord.value = state.best_word ?? null;
  };

  /**
   * Map snapshot status to AppGameState.
   */
  const mapGameState = (status: GameSnapshotState['status']): AppGameState => {
    switch (status) {
      case 'SelectingLetters':
        return 'selecting-letters';
      case 'InProgress':
        return 'in-progress';
      case 'BetweenRounds':
        return 'round-finished';
      case 'Finalized':
        return 'game-over';
      case 'WaitingForOpponent':
        return 'matchmaking';
      default:
        reportClientError(
          new Error(`mapGameState: unknown status "${status}"`),
          ErrorLocation.GameFlowUnknownStatus
        );
        return 'error';
    }
  };

  /**
   * Handle event-specific side effects after state is applied.
   */
  const triggerTransition = (snapshot: GameSnapshot) => {
    const { event, state } = snapshot;

    switch (event) {
      case 'GameStarted': {
        clearMatchFoundTimeout();
        selectionPhaseStart = Date.now();
        // Skip transition screen on reconnection (no prior OpponentFound)
        if (!matchFoundTimestamp) {
          gameState.value = 'selecting-letters';
          break;
        }
        const elapsed = Date.now() - matchFoundTimestamp;
        const remaining = GAME_CONFIG.MATCH_FOUND_DISPLAY_MS - elapsed;

        if (remaining > 0) {
          gameState.value = 'match-found';
          matchFoundTimeoutId = setTimeout(() => {
            if (gameState.value === 'match-found') {
              gameState.value = 'selecting-letters';
            }
          }, remaining);
        } else {
          gameState.value = 'selecting-letters';
        }
        break;
      }

      case 'LetterSelected':
        if (gameState.value === 'match-found') {
          clearMatchFoundTimeout();
        }
        if (!selectionPhaseStart) {
          selectionPhaseStart = Date.now();
        }
        gameState.value = 'selecting-letters';
        break;

      case 'SelectionComplete':
        // Selection is factually complete, but stay in selecting-letters
        // until TimerStarted arrives (gated by ReadyForFinding on server).
        // Record the selection-phase metric.
        if (selectionPhaseStart && gameState.value === 'selecting-letters') {
          getMetrics().uiLetterSelectionDurationMs.record(Date.now() - selectionPhaseStart);
          selectionPhaseStart = null;
        }
        break;

      case 'TimerStarted':
      case 'TimerEnded':
      case 'PlayerSubmitted':
        if (selectionPhaseStart && gameState.value === 'selecting-letters') {
          getMetrics().uiLetterSelectionDurationMs.record(Date.now() - selectionPhaseStart);
          selectionPhaseStart = null;
        }
        gameState.value = 'in-progress';
        break;

      case 'RoundCompleted':
        roundResults.value = state.round_results;
        gameState.value = 'round-finished';
        break;

      case 'NewRoundRequested':
        opponentWantsNewRound.value = true;
        // Don't change gameState - stay in current state
        break;

      case 'NewRoundStarting':
        opponentWantsNewRound.value = false;
        roundResults.value = null;
        onResetTimerPhase();
        stopTimer();
        selectionPhaseStart = Date.now();
        gameState.value = 'selecting-letters';
        break;

      case 'GameFinalized':
        roundResults.value = state.round_results;
        stopTimer();
        gameState.value = 'game-over';
        break;

      case 'OpponentEndedGame':
        roundResults.value = state.round_results;
        stopTimer();
        gameState.value = 'game-over';
        break;

      case 'StateRestored':
        // Restore game state based on status
        gameState.value = mapGameState(state.status);
        if (state.status === 'BetweenRounds' || state.status === 'Finalized') {
          roundResults.value = state.round_results;
        }
        break;

      default:
        getMetrics().gameStateMismatch.add(1);
        reportClientError(
          new Error(`triggerTransition: unknown event tag "${event}"`),
          ErrorLocation.GameFlowUnknownEvent
        );
        handleError(`Unexpected game event: ${event}`);
        break;
    }
  };

  /**
   * Main snapshot handler - entry point for all GameSnapshot messages.
   */
  const handleSnapshot = (snapshot: GameSnapshot) => {
    // After handleReturnHome sends EndGame and resets to matchmaking, the
    // server echoes back GameFinalized asynchronously. Without this guard,
    // applyState re-populates gameId and triggerTransition puts the UI back
    // into game-over, causing a second "return home" click to send EndGame
    // after the server already cleared the player context.
    if (
      awaitingEndGameResponse &&
      (snapshot.event === 'GameFinalized' || snapshot.event === 'OpponentEndedGame')
    ) {
      awaitingEndGameResponse = false;
      return;
    }
    applyState(snapshot.state);
    triggerTransition(snapshot);
  };

  const handlePlayAgain = () => {
    opponentWantsNewRound.value = false;
    if (gameId.value) {
      const sent = sendMessage({ NewRoundRequest: { game_id: gameId.value } });
      if (sent) {
        gameState.value = 'awaiting-new-round';
      } else {
        handleError('Connection lost. Returning to matchmaking.');
      }
    } else {
      resetGameState();
    }
  };

  const handleReturnHome = () => {
    if (gameId.value) {
      awaitingEndGameResponse = true;
      const sent = sendMessage({ EndGame: { game_id: gameId.value } });
      if (!sent) {
        awaitingEndGameResponse = false;
        handleError('Connection lost. Returning to matchmaking.');
        return;
      }
    }
    resetGameState();
  };

  const handleFindNewMatch = () => {
    resetGameState();
    autoStartMatchmaking.value = true;
  };

  const handleError = (error: string) => {
    errorMessage.value = error;
    gameState.value = 'error';
  };

  const handleReconnect = () => {
    resetGameState();
    autoStartMatchmaking.value = true;
  };

  const handleQueueStatus = (available: boolean) => {
    queueAvailable.value = available;
  };

  const handleWaitingPlayerNotFound = () => {
    clearTryMatchTimeout();
    if (tryingMatch.value) {
      tryingMatch.value = false;
      queueAvailable.value = false;
      showRaceConditionDialog.value = true;
    }
  };

  const handleTryMatchWaitingPlayer = () => {
    tryingMatch.value = true;
    sendMessage({ TryMatchWaitingPlayer: {} });
    // Guard against a server that never responds: reset the seek flag after a
    // timeout so the banner is restored and the player can retry or continue.
    tryMatchTimeoutId = setTimeout(() => {
      tryMatchTimeoutId = null;
      if (tryingMatch.value) {
        tryingMatch.value = false;
        queueAvailable.value = true;
      }
    }, TRY_MATCH_TIMEOUT_MS);
  };

  const handlePlaySolo = () => {
    showRaceConditionDialog.value = false;
    if (gameId.value) {
      const sent = sendMessage({ NewRoundRequest: { game_id: gameId.value } });
      if (sent) {
        gameState.value = 'awaiting-new-round';
      } else {
        handleError('Connection lost. Returning to matchmaking.');
      }
    } else {
      resetGameState();
    }
  };

  const handleFindNewMatchFromDialog = () => {
    showRaceConditionDialog.value = false;
    handleFindNewMatch();
  };

  onScopeDispose(() => {
    clearMatchFoundTimeout();
    clearTryMatchTimeout();
  });

  return {
    gameState: shallowReadonly(gameState),
    gameId: shallowReadonly(gameId),
    players: shallowReadonly(players),
    currentPlayerId: shallowReadonly(currentPlayerId),
    selectedLetters: shallowReadonly(selectedLetters),
    roundResults: shallowReadonly(roundResults),
    errorMessage: shallowReadonly(errorMessage),
    newRoundPending,
    opponentWantsNewRound: shallowReadonly(opponentWantsNewRound),
    cumulativeScores: shallowReadonly(cumulativeScores),
    roundNumber: shallowReadonly(roundNumber),
    wordSubmitted: shallowReadonly(wordSubmitted),
    opponentSubmitted: shallowReadonly(opponentSubmitted),
    selectorPlayerId: shallowReadonly(selectorPlayerId),
    selectionEnabled: shallowReadonly(selectionEnabled),
    vowelButtonEnabled: shallowReadonly(vowelButtonEnabled),
    consonantButtonEnabled: shallowReadonly(consonantButtonEnabled),
    selectionStatus: shallowReadonly(selectionStatus),
    roundEndedEarly: shallowReadonly(roundEndedEarly),
    autoStartMatchmaking: shallowReadonly(autoStartMatchmaking),
    matchFoundInfo: shallowReadonly(matchFoundInfo),
    bestWord: shallowReadonly(bestWord),
    queueAvailable: shallowReadonly(queueAvailable),
    showRaceConditionDialog: shallowReadonly(showRaceConditionDialog),
    recordMatchFound,
    handleSnapshot,
    handlePlayAgain,
    handleReturnHome,
    handleFindNewMatch,
    handleError,
    handleReconnect,
    handleQueueStatus,
    handleTryMatchWaitingPlayer,
    handleWaitingPlayerNotFound,
    handlePlaySolo,
    handleFindNewMatchFromDialog,
  };
}
